/**
 * Security Headers Interceptor
 *
 * Adds security-related HTTP headers to outgoing requests.
 * These headers help protect against various attack vectors.
 *
 * Headers added:
 * - X-Requested-With: XMLHttpRequest - Identifies requests as AJAX
 *
 * Note: Most security headers (like CSP, X-Frame-Options, HSTS, X-Content-Type-Options)
 * should be set by the server, as they protect the response rather than the request.
 * Sending these headers in requests can cause CORS issues if the server doesn't
 * explicitly allow them in Access-Control-Allow-Headers.
 *
 * @example
 * ```typescript
 * // Headers are automatically added to all requests
 * this.http.get('/api/data').subscribe();
 * // Request will include X-Requested-With header
 * ```
 */

import { HttpInterceptorFn, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { SECURITY_CONFIG } from '../constants/security.constants';

/**
 * Security Headers Interceptor Function
 *
 * Functional interceptor that adds security headers to all requests.
 * Uses Angular 15+ functional interceptor pattern.
 *
 * @param req - HTTP request
 * @param next - HTTP handler function
 * @returns Observable of HTTP event
 */
export const securityHeadersInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
) => {
  // Clone request and add security headers
  const clonedReq = req.clone({
    setHeaders: {
      ...SECURITY_CONFIG.SECURITY_HEADERS
    }
  });

  return next(clonedReq);
};
